import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Alert, Box, Button, ButtonBase, Collapse, IconButton, InputAdornment, ListItemIcon, ListItemText,
    Menu, MenuItem, Popover, Stack, TextField, Typography,
} from '@mui/material';
import {useEffect, useId, useRef, useState, type Ref} from 'react';
import {useTranslation} from 'react-i18next';
import {variableChipSx} from '../../../../../common/variables/variableChipStyles';
import {isEmailPlaceholderNameAvailable, type EmailPlaceholder, type EmailPlaceholderEdit} from '../../../config/emailPlaceholders';
import type {AvailableVariable} from '../../../config/nodeVariables';
import {automationVariableLabel} from '../../../config/variableReferences';
import AutomationVariablePicker from '../variables/AutomationVariablePicker';
import {referenceOptions} from '../variables/automationVariableOptions';
import {readAutomationVariableReference} from '../variables/automationVariableText';

interface Props {
    mappings: EmailPlaceholder[];
    onEdit: (edit: EmailPlaceholderEdit) => void;
    sources: AvailableVariable[];
    readOnly?: boolean;
}

type RowAnchor = {anchor: HTMLElement; index: number};

function VariableNameField({name, inputRef, disabled, isAvailable, onRename}: {
    name: string;
    inputRef: Ref<HTMLInputElement>;
    disabled: boolean;
    isAvailable: (name: string) => boolean;
    onRename: (name: string) => void;
}) {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(name);
    useEffect(() => setDraft(name), [name]);
    const invalid = draft !== name && !isAvailable(draft);

    return <TextField size={'small'} fullWidth label={t('Variable name')} value={draft}
                      inputRef={inputRef} disabled={disabled} error={invalid}
                      helperText={invalid ? t('Use a unique name without brackets or line breaks.') : undefined}
                      onChange={event => setDraft(event.target.value)}
                      onBlur={() => {
                          // A partial or invalid edit never replaces a working name in the email.
                          if (!invalid && draft.trim()) onRename(draft.trim());
                          else setDraft(name);
                      }}
                      onKeyDown={event => {
                          if (event.key === 'Enter') {
                              event.preventDefault();
                              if (!invalid) (event.target as HTMLInputElement).blur();
                          }
                          if (event.key === 'Escape') {
                              event.stopPropagation();
                              setDraft(name);
                          }
                      }}
                      InputLabelProps={{shrink: true}} sx={{'& input': {color: variableChipSx.color}}}/>;
}

/** Every new mapping starts with a step value; its reusable name is seeded when that value is chosen. */
const EmailVariablesEditor = ({mappings, onEdit, sources, readOnly = false}: Props) => {
    const {t} = useTranslation();
    const id = useId();
    const [expanded, setExpanded] = useState<number[]>([]);
    const [picker, setPicker] = useState<{anchor: HTMLElement; index: number | null} | null>(null);
    const [menu, setMenu] = useState<RowAnchor | null>(null);
    const nameRefs = useRef<(HTMLInputElement | null)[]>([]);
    const focusNewRow = useRef(false);
    const closePicker = () => {
        const target = picker?.anchor.querySelector('input') ?? picker?.anchor;
        setPicker(null);
        target?.focus();
    };

    useEffect(() => {
        if (!focusNewRow.current) return;
        const input = nameRefs.current[mappings.length - 1];
        input?.focus();
        input?.select();
        focusNewRow.current = false;
    }, [mappings.length]);

    return <Stack component={'section'} aria-labelledby={`${id}-title`} gap={1.5} sx={{minWidth: 0}}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <Typography id={`${id}-title`} variant={'body1'} fontWeight={500}>
                {t('Variables')} ({mappings.length})
            </Typography>
            <Button size={'small'} startIcon={<AddIcon/>} disabled={readOnly || !sources.length}
                    onClick={event => setPicker({anchor: event.currentTarget, index: null})}>{t('Add variable')}</Button>
        </Stack>
        <Alert severity={'info'}>
            {t('Variables come from earlier steps connected to this email. Choose Add variable, select a step value and name it, then use / or Insert variable in the subject or content.')}
        </Alert>
        {!sources.length && <Typography variant={'body2'} color={'text.secondary'}>
            {t('Connect an earlier step to this email to choose variable values.')}
        </Typography>}
        <Stack>
            {mappings.map((mapping, index) => {
                const reference = readAutomationVariableReference(mapping.value);
                const sourceMissing = !!reference && !sources.some(variable => variable.nodeName === reference.nodeName);
                const open = expanded.includes(index);
                return <Box key={index} sx={{borderBottom: 1, borderColor: 'divider', py: 0.5}}>
                    <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                        <ButtonBase aria-expanded={open} aria-controls={`${id}-mapping-${index}`}
                                    aria-label={t('Edit variable {{name}}', {name: mapping.key})}
                                    onClick={() => setExpanded(current => open ? current.filter(row => row !== index) : [...current, index])}
                                    sx={{minWidth: 0, flex: 1, justifyContent: 'flex-start', gap: 1, py: 1,
                                        '&:focus-visible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2}}}>
                            {open ? <ExpandLessIcon fontSize={'small'} sx={{color: 'text.secondary'}}/> : <ExpandMoreIcon fontSize={'small'} sx={{color: 'text.secondary'}}/>}
                            <Box component={'span'} sx={{...variableChipSx, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                {mapping.key || t('Unnamed variable')}
                            </Box>
                            {sourceMissing && <Typography variant={'body2'} color={'error.main'}>{t('Source unavailable')}</Typography>}
                        </ButtonBase>
                        <IconButton size={'small'} disabled={readOnly} aria-label={t('Variable options')}
                                    onClick={event => setMenu({anchor: event.currentTarget, index})}>
                            <MoreVertIcon fontSize={'small'}/>
                        </IconButton>
                    </Stack>
                    <Collapse in={open} timeout={0} unmountOnExit>
                        <Stack gap={1.5} id={`${id}-mapping-${index}`} sx={{pt: 1, pb: 1.5}}>
                            <TextField size={'small'} fullWidth label={t('Step value')} disabled={readOnly || !sources.length}
                                           error={sourceMissing}
                                           helperText={sourceMissing ? t('Connect ‘{{step}}’ before this email to use this value.', {step: reference.nodeName}) : undefined}
                                           value={reference ? automationVariableLabel(reference) : mapping.value}
                                           placeholder={t('Select step value')}
                                           onClick={event => { if (!readOnly && sources.length) setPicker({anchor: event.currentTarget, index}); }}
                                           onKeyDown={event => {
                                               if (!readOnly && sources.length && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
                                                   event.preventDefault();
                                                   setPicker({anchor: event.currentTarget, index});
                                               }
                                           }}
                                           InputLabelProps={{shrink: true}}
                                           inputProps={{'aria-haspopup': 'menu', title: reference ? automationVariableLabel(reference) : undefined}}
                                           InputProps={{readOnly: true, endAdornment: <InputAdornment position={'end'}>
                                               <ArrowDropDownIcon sx={{color: 'text.secondary'}}/>
                                           </InputAdornment>}}
                                           sx={{minWidth: 0,
                                               '& .MuiInputBase-root, & input': {cursor: readOnly || !sources.length ? 'default' : 'pointer'},
                                               '& input': {textOverflow: 'ellipsis', color: variableChipSx.color}}}/>
                            <VariableNameField name={mapping.key} disabled={readOnly || !mapping.value}
                                               inputRef={element => { nameRefs.current[index] = element; }}
                                               isAvailable={name => isEmailPlaceholderNameAvailable(name, mappings, index)}
                                               onRename={key => onEdit({type: 'rename', index, key})}/>
                        </Stack>
                    </Collapse>
                </Box>;
            })}
        </Stack>
        <Popover open={!readOnly && !!sources.length && !!picker} anchorEl={picker?.anchor} onClose={closePicker}
                 anchorOrigin={{vertical: 'bottom', horizontal: 'left'}} transitionDuration={0} disableRestoreFocus
                 slotProps={{paper: {sx: {mt: 0.5, borderRadius: 2}}}}>
            <AutomationVariablePicker purpose={'select-step-value'} options={referenceOptions(sources)}
                                      onSelect={option => {
                                          if (!picker || option.kind !== 'reference') return;
                                          if (picker.index === null) {
                                              focusNewRow.current = true;
                                              setExpanded(current => [...current, mappings.length]);
                                              onEdit({type: 'add', variable: option.variable});
                                          } else {
                                              onEdit({type: 'select-source', index: picker.index, variable: option.variable});
                                              closePicker();
                                          }
                                          setPicker(null);
                                      }}/>
        </Popover>
        <Menu open={!readOnly && !!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
            <MenuItem onClick={() => {
                if (menu) {
                    const removedIndex = menu.index;
                    setExpanded(current => current.filter(index => index !== removedIndex).map(index => index > removedIndex ? index - 1 : index));
                    onEdit({type: 'remove', index: removedIndex});
                }
                setMenu(null);
            }}>
                <ListItemIcon><DeleteOutlineIcon fontSize={'small'}/></ListItemIcon>
                <ListItemText>{t('Remove variable')}</ListItemText>
            </MenuItem>
        </Menu>
    </Stack>;
};

export default EmailVariablesEditor;
